import React, { useState } from "react";
import {
  Alert,
  AlertActions,
  AlertBody,
  AlertDescription,
  AlertTitle,
} from "../design/alert";
import { Button } from "../design/button";
import { Input } from "../design/input";
import { Field, Label, Description } from "../design/fieldset";
import { Case } from "@visitproof/types";
import {
  ExclamationTriangleIcon,
  TrashIcon,
  CalendarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

interface DeleteCaseDialogProps {
  caseItem: Case;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

interface WarningItemProps {
  icon: React.ReactNode;
  label: string;
  count: number;
}

const WarningItem: React.FC<WarningItemProps> = ({ icon, label, count }) => {
  if (count === 0) return null;

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="flex-shrink-0 text-amber-500">{icon}</span>
      <span className="text-zinc-700 dark:text-zinc-300">
        <span className="font-semibold">{count}</span> {label}
        {count !== 1 ? "s" : ""}
      </span>
    </div>
  );
};

const DeleteCaseDialog: React.FC<DeleteCaseDialogProps> = ({
  caseItem,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const caseIdentifier = caseItem.identifier || "this case";
  const confirmPhrase = "DELETE";

  // Calculate counts for warnings
  const visitCount =
    (caseItem.numberOfScheduledVisits ?? 0) +
    (caseItem.numberOfCompletedVisits ?? 0) +
    (caseItem.numberOfCanceledVisits ?? 0);
  const parentCount = Object.keys(caseItem.parents ?? {}).length;
  const childCount = Object.keys(caseItem.children ?? {}).length;

  const hasAssociatedData = visitCount > 0 || parentCount > 0 || childCount > 0;

  const handleConfirm = async () => {
    if (confirmText !== confirmPhrase) return;

    setIsDeleting(true);
    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error("Failed to delete case:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setConfirmText("");
    onClose();
  };

  return (
    <Alert open={isOpen} onClose={handleClose} size="lg">
      <div className="flex items-start gap-4">
        <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <AlertTitle>Delete Case {caseIdentifier}?</AlertTitle>
          <AlertDescription>
            This action is permanent and cannot be undone. All data associated
            with this case will be permanently deleted.
          </AlertDescription>
        </div>
      </div>

      <AlertBody>
        {hasAssociatedData && (
          <div className="p-4 mb-4 border rounded-lg bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="font-semibold text-amber-800 dark:text-amber-200">
                Warning: This case contains data
              </span>
            </div>
            <div className="space-y-2">
              <WarningItem
                icon={<CalendarIcon className="w-4 h-4" />}
                label="visit"
                count={visitCount}
              />
              <WarningItem
                icon={<UserGroupIcon className="w-4 h-4" />}
                label="parent"
                count={parentCount}
              />
              <WarningItem
                icon={<UserGroupIcon className="w-4 h-4" />}
                label="child"
                count={childCount}
              />
            </div>
          </div>
        )}

        <div className="p-4 border rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700">
          <h4 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">
            The following will be permanently deleted:
          </h4>
          <ul className="space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
            <li className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-red-500" />
              All visit records and history
            </li>
            <li className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-red-500" />
              All reports and documentation
            </li>
            <li className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-red-500" />
              All invoices and payment records
            </li>
            <li className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-red-500" />
              All uploaded files and photos
            </li>
            <li className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-red-500" />
              Parent and child associations
            </li>
          </ul>
        </div>

        <Field className="mt-4">
          <Label>Type "{confirmPhrase}" to confirm</Label>
          <Description>
            This extra step helps prevent accidental deletion
          </Description>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder={confirmPhrase}
            disabled={isDeleting}
            autoComplete="off"
          />
        </Field>
      </AlertBody>

      <AlertActions>
        <Button plain onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          color="red"
          onClick={handleConfirm}
          disabled={confirmText !== confirmPhrase || isDeleting}
        >
          {isDeleting ? "Deleting..." : "Permanently Delete Case"}
        </Button>
      </AlertActions>
    </Alert>
  );
};

export default DeleteCaseDialog;
